import React, { useEffect, useState } from "react";
import { TextField, CircularProgress, Box } from "@mui/material";
import { MdInfoOutline } from "react-icons/md";
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider.tsx";
import { useEndpointProvider } from "../../../Providers/EndpointProvider";
import { useEnvironmentVariablesProvider } from "../../../Providers/EnvironmentVariablesProvider.tsx";
import { DEFAULT_PATIENT_ID_LENGTH, DEFAULT_PATIENT_ID_TYPE } from "../../Data/Visualize/UploadsSubpages/UploadWindows/PatientIDType"
import { MobergFontFamily } from "../../../Components/MobergFont/MobergFont";
import { MobergButton, MobergButtonShape, MobergButtonVariant, MobergTheme } from "../../../Moberg";
import MobergSnackbar from "../../../Components/MobergSnackbar/MobergSnackbar";
import RMSelectPatient from "./RMSelectPatient";
import { usePatientValidation } from "../hooks/usePatientValidation";

const RMAddPatient = (props) => {
    const { gatewayId: gatewayIdProp, onNext: onNextProp, onBack: onBackProp, onClose, exposeHandlers, isModal = false } = props;
    const [activeTab, setActiveTab] = useState(0);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    
    const environmentVariablesProvider = useEnvironmentVariablesProvider();
    const PATIENT_ID_LENGTH = environmentVariablesProvider.environmentVariables.PATIENT_ID_LENGTH || DEFAULT_PATIENT_ID_LENGTH;
    const PATIENT_ID_MIN_LENGTH = Math.min(environmentVariablesProvider.environmentVariables.PATIENT_ID_MIN_LENGTH || PATIENT_ID_LENGTH, PATIENT_ID_LENGTH);
    const PATIENT_ID_TYPE = environmentVariablesProvider.environmentVariables.PATIENT_ID_TYPE || DEFAULT_PATIENT_ID_TYPE;
    
    const { LINKS } = useBackendLinksProvider();
    const endpointProvider = useEndpointProvider();
    
    // Use custom validation hook
    const {
        patientId,
        isLoading,
        validationMessage,
        isValid,
        existingPatientData,
        isPatientAdmitted,
        validatePatientId
    } = usePatientValidation({
        patientIdMinLength: PATIENT_ID_MIN_LENGTH,
        patientIdLength: PATIENT_ID_LENGTH,
        patientIdType: PATIENT_ID_TYPE,
        checkIdentifier: async (value) => {
            return await endpointProvider.post(LINKS.DATA.UPLOAD.CHECK_PATIENT_IDENTIFIER, {
                site_id: environmentVariablesProvider.environmentVariables.SITE_ID,
                project_id: environmentVariablesProvider.environmentVariables.PROJECT_ID,
                patient_identifier: value
            });
        },
        getAttributes: async (patientKey) => {
            return await endpointProvider.post(LINKS.DATA.PROFILING.GET_PATIENT_ATTRIBUTES, {
                patient_primary_key: patientKey
            });
        }
    });

    const handlePatientIdChange = async (e) => {
        const value = e.target.value;
        
        try {
            await validatePatientId(value);
        } catch (err) {
            setErrorMessage("Error checking patient ID. Please try again.");
            setShowError(true);
        }
    };

    const handleNext = () => {
        if (!patientId || !isValid) {
            setShowError(true);
            setErrorMessage("Please enter a valid Patient ID");
            return;
        }
        
        if (onNextProp) {
            onNextProp(patientId, firstName, lastName);
        }
    };

    const handleBack = () => {
        if (onBackProp) {
            onBackProp();
        }
    };

    useEffect(() => {
        if (isModal && exposeHandlers) {
            exposeHandlers({
                onNext: handleNext,
                onBack: handleBack,
                isNextDisabled: activeTab === 0 ? (!patientId || isLoading || !isValid) : true
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModal, exposeHandlers, patientId, isLoading, isValid, firstName, lastName, activeTab, existingPatientData, isPatientAdmitted]);

    const handleSelectExistingPatient = () => {
        if (!existingPatientData) return;
        
        // Pass patient data to parent - parent will handle the confirmation modal
        if (onNextProp) {
            onNextProp(
                existingPatientData.patient_id,
                existingPatientData.patient_name?.split(/\s+/)[0] || "",
                existingPatientData.patient_name?.split(/\s+/).slice(1).join(" ") || ""
            );
        }
    };

    const tabStyle = (isActive) => ({
        padding: "12px 24px",
        fontSize: "15px",
        fontWeight: isActive ? "600" : "400",
        color: isActive ? "var(--color-accent-interactive)" : "var(--text-secondary-color)",
        background: "none",
        border: "none",
        borderBottom: isActive ? "3px solid var(--color-accent-interactive)" : "3px solid transparent",
        cursor: "pointer",
        fontFamily: MobergFontFamily.REGULAR,
        transition: "all 0.2s ease"
    });

    return (
        <div style={{ backgroundColor: "var(--color-surface-primary)", minHeight: isModal ? "auto" : "calc(100vh - 60px)", height: "100%", display: "flex", flexDirection: "column" }}>
            <div style={{
                display: "flex",
                justifyContent: "center",
                borderBottom: "1px solid var(--color-border-light)",
                backgroundColor: "var(--color-surface-primary)",
                padding: "0 30px"
            }}>
                <button 
                    style={tabStyle(activeTab === 0)}
                    onClick={() => setActiveTab(0)}
                >
                    Admit a new patient
                </button>
                <button 
                    style={tabStyle(activeTab === 1)}
                    onClick={() => setActiveTab(1)}
                >
                    Assign to an existing patient
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 0 ? (
                <div style={{ padding: '16px 30px 40px', flex: 1, paddingTop: '16px', display: 'flex', justifyContent: 'center' }}>

                    <div style={{              
                        padding: "16px",
                        width: "50%",
                        display: "flex",
                        flexDirection: "column",
                        
                    }}>
                        {/* Instructions */}
                        <div style={{ 
                            marginBottom: '16px'
                        }}>
                            <div style={{
                                backgroundColor: 'var(--color-hover-neutral)',
                                borderRadius: '4px',
                                padding: '14px 18px',
                                fontSize: '13.5px',
                                color: 'var(--color-text-primary)',
                                lineHeight: '1.6',
                                letterSpacing: '-0.01em',
                                fontWeight: '400'
                            }}>
                                Enter the patient's information below to create a new patient record. Patient ID is required and must be unique.
                            </div>
                        </div>

                        {/* Patient Information Card */}
                        <div style={{ 
                            backgroundColor: "var(--color-surface-panel)",
                            borderRadius: "4px",
                            padding: "24px 32px 32px",
                            // boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                            flex: 1
                        }}>
                        <h3 style={{
                            fontSize: "18px",
                            fontWeight: "600",
                            color: "var(--color-text-primary)",
                            margin: "0 0 4px 0",
                            fontFamily: MobergFontFamily.HEADER,
                            textAlign: "left"
                        }}>
                            Patient Information
                        </h3>
                        <p style={{
                            fontSize: "13px",
                            color: "var(--text-secondary-color)",
                            margin: "0 0 16px 0",
                            fontFamily: MobergFontFamily.REGULAR
                        }}>
                            Patient ID is required.
                        </p>
                        
                        {/* Divider */}
                        <div style={{ 
                            borderBottom: "1px solid var(--color-border-light)",
                            marginBottom: "24px" 
                        }} />

                        {/* Patient ID */}
                        <div style={{ 
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "32px",
                            marginBottom: "24px"
                        }}>
                            <div>
                                <label style={{ 
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "var(--color-text-primary)",
                                    display: "block",
                                    marginBottom: "8px",
                                    fontFamily: MobergFontFamily.REGULAR
                                }}>
                                    Patient ID <span style={{ color: "red" }}>*</span>
                                </label>
                                <TextField
                                    placeholder={PATIENT_ID_MIN_LENGTH === PATIENT_ID_LENGTH
                                        ? `${PATIENT_ID_LENGTH} characters required`
                                        : `${PATIENT_ID_MIN_LENGTH}-${PATIENT_ID_LENGTH} characters required`}
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    value={patientId}
                                    onChange={(e) => handlePatientIdChange(e)}
                                    inputProps={{ maxLength: PATIENT_ID_LENGTH }}
                                    error={showError || (!isValid && validationMessage !== "")}
                                    helperText={
                                        <span style={{
                                            color: showError ? 'red' :
                                                (validationMessage === "Patient ID is available" ? 'green' : 'red'),
                                            fontWeight: "400",
                                            fontSize: "12px",
                                            visibility: (showError || validationMessage) ? 'visible' : 'hidden'
                                        }}>
                                            {showError ? "Patient ID is required" : (validationMessage || "Placeholder")}
                                        </span>
                                    }
                                    InputProps={{
                                        endAdornment: isLoading && (
                                            <Box display="flex" alignItems="center" mr={1}>
                                                <CircularProgress size={20} />
                                            </Box>
                                        )
                                    }}
                                />
                            </div>
                            
                            {/* Existing Patient Selection Card */}
                            {existingPatientData && (
                                <div style={{
                                    backgroundColor: "var(--color-hover-neutral)",
                                    borderRadius: "4px",
                                    padding: "16px 20px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "12px",
                                    
                                }}>
                                    <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                        <MdInfoOutline size={20} style={{ color: "var(--color-accent-interactive)", marginTop: "2px", flexShrink: 0 }} />
                                        <div style={{ flex: 1 }}>
                                            <p style={{
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                color: "var(--color-text-primary)",
                                                margin: "0 0 4px 0",
                                                fontFamily: MobergFontFamily.REGULAR,
                                                lineHeight: "1.4"
                                            }}>
                                                Patient {existingPatientData.patient_id} already exists
                                            </p>
                                            <p style={{
                                                fontSize: "13px",
                                                color: "var(--text-secondary-color)",
                                                margin: "0 0 8px 0",
                                                fontFamily: MobergFontFamily.REGULAR,
                                                lineHeight: "1.5"
                                            }}>
                                                <strong>Patient name:</strong> {existingPatientData.patient_name}
                                            </p>
                                            <p style={{
                                                fontSize: "12px",
                                                color: "var(--text-secondary-color)",
                                                margin: "0",
                                                fontFamily: MobergFontFamily.REGULAR,
                                                lineHeight: "1.5"
                                            }}>
                                                Would you like to assign this patient to the selected MCP device?
                                            </p>
                                        </div>
                                    </div>
                                    <MobergButton
                                        variant={MobergButtonVariant.FILLED}
                                        theme={MobergTheme.BLUE}
                                        shape={MobergButtonShape.RECTANGLE}
                                        onClick={handleSelectExistingPatient}
                                        style={{ 
                                            width: "100%",
                                            height: "40px",
                                            fontSize: "14px",
                                            fontWeight: "600"
                                        }}
                                    >
                                        Select Patient
                                    </MobergButton>
                                </div>
                            )}
                        </div>

                        {/* Divider */}
                        <div style={{ 
                            borderBottom: "1px solid var(--color-border-light)",
                            marginBottom: "16px" 
                        }} />
                        
                        <p style={{
                            fontSize: "13px",
                            color: "var(--text-secondary-color)",
                            margin: "0 0 16px 0",
                            fontFamily: MobergFontFamily.REGULAR
                        }}>
                            Optional
                        </p>

                        {/* First Name and Last Name */}
                        <div style={{ 
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "32px"
                        }}>
                            <div>
                                <label style={{ 
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "var(--color-text-primary)",
                                    display: "block",
                                    marginBottom: "8px",
                                    fontFamily: MobergFontFamily.REGULAR
                                }}>
                                    First name
                                </label>
                                <TextField
                                    placeholder="Enter first name"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                />
                            </div>

                            <div>
                                <label style={{ 
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "var(--color-text-primary)",
                                    display: "block",
                                    marginBottom: "8px",
                                    fontFamily: MobergFontFamily.REGULAR
                                }}>
                                    Last name
                                </label>
                                <TextField
                                    placeholder="Enter last name"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                />
                            </div>
                        </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ flex: 1, overflow: "hidden" }}>
                    <RMSelectPatient
                        gatewayId={gatewayIdProp}
                        onNext={onNextProp}
                        onBack={() => setActiveTab(0)}
                        onClose={onClose}
                        isModal={isModal}
                        hideHeader={true}
                    />
                </div>
            )}
            
            {/* Error Notification */}
            <MobergSnackbar
                open={showError}
                onClose={() => setShowError(false)}
                message={errorMessage}
                severity="error"
            />
        </div>
    );
};

export default RMAddPatient;
